import { Box, Button, Container, Grid, Paper, Typography } from '@mui/material';
import PetProfile from 'happypaws-shared/src/Pets/models/PetProfile';
import { PetType } from 'happypaws-shared/src/Pets/models/PetType';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import SkeletonLoader from '../../../components/common/SkeletonLoader';
import { useDeletePetPhoto } from '../../../hooks/pets/useDeletePetPhoto';
import { useGetPet } from '../../../hooks/pets/useGetSinglePet';
import { useUpdatePet } from '../../../hooks/pets/useUpdatePet';
import { useUploadPetPhoto } from '../../../hooks/pets/useUploadPetPhoto';
import {
  CatSpecific,
  DogSpecific,
  OtherPetsSpecific,
  PetFormBody,
  petTypeSelection,
} from './components/PetFormShared';

const EditPetProfile: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  const { data: fetchedPet, isLoading } = useGetPet(id!);
  const { mutate: updatePet, isPending } = useUpdatePet();
  const { mutate: uploadPetPhoto, isPending: isUploading } = useUploadPetPhoto();
  const { mutate: deletePetPhoto } = useDeletePetPhoto();

  const [pet, setPet] = useState<PetProfile | null>(null);

  const mainPhotoInputRef = useRef<HTMLInputElement | null>(null);
  const galleryInputRefs = useRef<(HTMLInputElement | null)[]>([]);

  // Sync fetched pet into local state once loaded
  useEffect(() => {
    if (fetchedPet) setPet(fetchedPet);
  }, [fetchedPet]);

  const updateCurrentPet = useCallback((updates: Partial<PetProfile>) => {
    setPet((prev) => (prev ? { ...prev, ...updates } : prev));
  }, []);

  const handleMainPhotoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !id) return;

    // Show preview immediately
    const previewUrl = URL.createObjectURL(file);
    updateCurrentPet({ photoUrl: previewUrl });

    // Upload and replace with real URL
    uploadPetPhoto(
      { petId: id, file },
      {
        onSuccess: (photoUrl) => {
          updateCurrentPet({ photoUrl });
        },
      }
    );
  };

  const handleGalleryPhotoUpload = (index: number) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !id) return;

    // Show preview immediately
    const previewUrl = URL.createObjectURL(file);
    const newGallery = [...(pet?.photoGallery ?? [null, null, null, null])] as (string | null)[];
    newGallery[index] = previewUrl;
    updateCurrentPet({ photoGallery: newGallery.filter(Boolean) as string[] });

    // Upload and replace with real URL
    uploadPetPhoto(
      { petId: id, file },
      {
        onSuccess: (photoUrl) => {
          setPet((prev) => {
            if (!prev) return prev;
            const updatedGallery = [...(prev.photoGallery ?? [])];
            const previewIndex = updatedGallery.indexOf(previewUrl);
            if (previewIndex !== -1) updatedGallery[previewIndex] = photoUrl;
            return { ...prev, photoGallery: updatedGallery };
          });
        },
      }
    );
  };

  const handleSave = () => {
    if (!pet?.petName?.trim()) {
      alert("Please enter your pet's name");
      return;
    }
    updatePet(
      { petId: id!, data: pet },
      {
        onSuccess: () => navigate('/my-pets'),
        onError: (error) => console.error('Failed to update pet:', error),
      }
    );
  };

  const handleDeleteMainPhoto = () => {
    if (!id || !pet?.photoUrl) return;
    deletePetPhoto({ petId: id, photoUrl: pet.photoUrl });
    updateCurrentPet({ photoUrl: null });
  };

  const handleDeleteGalleryPhoto = (index: number) => {
    if (!id || !pet?.photoGallery?.[index]) return;
    const photoUrl = pet.photoGallery[index];
    deletePetPhoto({ petId: id, photoUrl });
    updateCurrentPet({
      photoGallery: pet.photoGallery.filter((_, i) => i !== index),
    });
  };

  const petTypeFields = useMemo(() => {
    if (!pet) return null;
    switch (pet.type) {
      case PetType.DOG:
        return <DogSpecific currentPet={pet} updateCurrentPet={updateCurrentPet} />;
      case PetType.CAT:
        return <CatSpecific currentPet={pet} updateCurrentPet={updateCurrentPet} />;
      case PetType.OTHER:
        return <OtherPetsSpecific currentPet={pet} updateCurrentPet={updateCurrentPet} />;
      default:
        return null;
    }
  }, [pet, updateCurrentPet]);

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#FAF8F5', py: 4 }}>
      <Container maxWidth="md">
        <Typography
          variant="h4"
          sx={{ textAlign: 'center', fontWeight: 700, color: '#178B8E', mb: 1 }}
        >
          Edit Pet Profile
        </Typography>
        <Typography variant="body1" sx={{ textAlign: 'center', color: '#5A5A5A', mb: 4 }}>
          {pet ? `Editing ${pet.petName}` : 'Loading...'}
        </Typography>

        <SkeletonLoader isLoading={isLoading} count={1}>
          {pet && (
            <>
              {/* Pet Type Selection */}
              <Grid container spacing={2} sx={{ mb: 4 }}>
                {petTypeSelection.map(({ type, label, animal }) => (
                  <Grid size={{ xs: 12, sm: 4 }} key={type}>
                    <Paper
                      onClick={() => updateCurrentPet({ type })}
                      sx={{
                        p: 3,
                        textAlign: 'center',
                        cursor: 'pointer',
                        border: pet.type === type ? '3px solid #178B8E' : '2px solid #E0E0E0',
                        backgroundColor: pet.type === type ? 'rgba(23, 139, 142, 0.05)' : 'white',
                        transition: 'all 0.3s',
                        '&:hover': { transform: 'translateY(-4px)', boxShadow: 3 },
                      }}
                    >
                      <img
                        src={animal}
                        alt={label}
                        style={{ width: 125, height: 125, borderRadius: '8px' }}
                      />
                      <Typography sx={{ fontWeight: 600 }}>{label}</Typography>
                    </Paper>
                  </Grid>
                ))}
              </Grid>

              <Paper sx={{ p: 4, backgroundColor: 'white' }}>
                <PetFormBody
                  currentPet={pet}
                  updateCurrentPet={updateCurrentPet}
                  mainPhotoInputRef={mainPhotoInputRef}
                  galleryInputRefs={galleryInputRefs}
                  onMainPhotoUpload={handleMainPhotoUpload}
                  onGalleryPhotoUpload={handleGalleryPhotoUpload}
                  petTypeFields={petTypeFields}
                  onDeleteMainPhoto={handleDeleteMainPhoto}
                  onDeleteGalleryPhoto={handleDeleteGalleryPhoto}
                />

                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Button
                    variant="outlined"
                    fullWidth
                    size="large"
                    onClick={() => navigate('/my-pets')}
                    sx={{
                      py: 1.5,
                      fontSize: '1.1rem',
                      fontWeight: 600,
                      textTransform: 'none',
                      borderRadius: 2,
                      borderColor: '#178B8E',
                      color: '#178B8E',
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="contained"
                    fullWidth
                    size="large"
                    disabled={isPending || isUploading}
                    onClick={handleSave}
                    sx={{
                      backgroundColor: '#178B8E',
                      py: 1.5,
                      fontSize: '1.1rem',
                      fontWeight: 600,
                      textTransform: 'none',
                      borderRadius: 2,
                      '&:hover': { backgroundColor: '#0F6164' },
                    }}
                  >
                    {isPending ? 'Saving...' : isUploading ? 'Uploading photo...' : 'Save'}
                  </Button>
                </Box>
              </Paper>
            </>
          )}
        </SkeletonLoader>
      </Container>
    </Box>
  );
};

export default EditPetProfile;
